import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ChevronDown, Filter } from 'lucide-react'
import { toast } from 'sonner'

import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  getGetApplicationByIdQueryKey,
  getGetApprovedApplicationsWithDetailsQueryKey,
  useChangeApplicationStatus,
  useGetApprovedApplicationsWithDetails,
  useGetAllCategories,
  useRequestApplicationCorrection,
} from '@/utils/api/membership-service'
import type { ChangeApplicationStatusStatus } from '@/utils/api/membership-service/models/change-application-status-status'
import type { ChangeApplicationStatusParams } from '@/utils/api/membership-service/models/change-application-status-params'

import type { AdminApplication, AdminApplicationRow } from './types'
import { ApplicationDetailsDrawer } from './components/ApplicationDetailsDrawer'
import { createApprovedColumns } from './components/columns'
import { createApplicationRow, mapApplicationsResponse } from './utils'
import { ApplicationListSkeleton } from './components/ApplicationListSkeleton'

export function ApprovedApplicationsPage() {
  const queryClient = useQueryClient()
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [tablePagination, setTablePagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [selectedRenewalStatus, setSelectedRenewalStatus] = useState<string>('All')

  const renewalStatusOptions = ['All', 'Active', 'Expiring Soon (≤30 days)', 'Expired']

  const { pageIndex, pageSize } = tablePagination

  const categoriesQuery = useGetAllCategories({
    query: { staleTime: 1000 * 60 * 5 },
  })

  const categories = useMemo(() => {
    const apiCategories = categoriesQuery.data?.data ?? []
    const activeCategories = apiCategories
      .filter((cat) => cat.active)
      .map((cat) => cat.categoryName ?? '')
      .filter(Boolean)
    return ['All', ...activeCategories]
  }, [categoriesQuery.data])

  const applicationsQuery = useGetApprovedApplicationsWithDetails(
    { page: pageIndex, size: pageSize },
    {
      query: {
        select: (response) => {
          const payload = response.data
          const applications = mapApplicationsResponse(payload)

          return {
            applications,
            totalElements:
              typeof payload.totalElements === 'number' ? payload.totalElements : applications.length,
            pageIndex: typeof payload.number === 'number' ? payload.number : pageIndex,
            pageSize: typeof payload.size === 'number' ? payload.size : pageSize,
          }
        },
      },
    },
  )

  const changeStatusMutation = useChangeApplicationStatus({
    mutation: {
      onSuccess: async (_data, variables) => {
        const status = variables.params.status
        const isApproved = status === 'APPROVED'
        toast.success(`Application ${isApproved ? 'approved' : 'rejected'} successfully`)
      },
    },
  })

  const requestCorrectionMutation = useRequestApplicationCorrection({
    mutation: {
      onSuccess: () => {
        toast.success('Correction request sent to applicant')
      },
    },
  })

  const applications = applicationsQuery.data?.applications ?? []
  const totalApplications = applicationsQuery.data?.totalElements ?? 0

  const filteredApplications = useMemo(
    () => {
      let filtered = applications
      
      // Filter by category
      if (selectedCategory !== 'All') {
        filtered = filtered.filter((app) => app.categoryName === selectedCategory)
      }
      
      // Filter by renewal status
      if (selectedRenewalStatus !== 'All') {
        filtered = filtered.filter((app) => {
          const expiryDate = app.membershipExpiryAt ? new Date(app.membershipExpiryAt) : null
          const today = new Date()
          
          if (!expiryDate) return false
          
          const daysRemaining = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
          
          switch (selectedRenewalStatus) {
            case 'Active':
              return daysRemaining > 30
            case 'Expiring Soon (≤30 days)':
              return daysRemaining > 0 && daysRemaining <= 30
            case 'Expired':
              return daysRemaining <= 0
            default:
              return true
          }
        })
      }
      
      return filtered
    },
    [applications, selectedCategory, selectedRenewalStatus],
  )

  const rows: AdminApplicationRow[] = useMemo(
    () => filteredApplications.map((application) => createApplicationRow(application)),
    [filteredApplications],
  )

  const applicationIndex = useMemo(() => {
    return new Map(applications.map((application) => [application.id, application] as const))
  }, [applications])

  const columns = useMemo(() => createApprovedColumns(), [])

  const selectedApplication = selectedApplicationId
    ? applicationIndex.get(selectedApplicationId)
    : undefined

  const handleRowClick = (row: AdminApplicationRow) => {
    setSelectedApplicationId(row.id)
    setDrawerOpen(true)
  }

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open)
    if (!open) {
      setSelectedApplicationId(null)
      changeStatusMutation.reset()
    }
  }

  const handleStatusChange = useCallback(
    async (
      status: ChangeApplicationStatusStatus,
      metadata?: { durationInMonths?: number; amount?: number; rejectionReason?: string },
    ) => {
      if (!selectedApplicationId) return
      try {
        const params: ChangeApplicationStatusParams = { status }

        if (typeof metadata?.durationInMonths === 'number') {
          params.months = metadata.durationInMonths
        }

        if (typeof metadata?.amount === 'number') {
          params.amount = Number(metadata.amount)
        }

        if (metadata?.rejectionReason) {
          params.rejectionReason = metadata.rejectionReason
        }

        await changeStatusMutation.mutateAsync({
          id: selectedApplicationId,
          params,
        })

        await Promise.all([
          applicationsQuery.refetch(),
          queryClient.invalidateQueries({
            queryKey: getGetApplicationByIdQueryKey(selectedApplicationId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetApprovedApplicationsWithDetailsQueryKey({
              page: pageIndex,
              size: pageSize,
            }),
          }),
        ])
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`Unable to update application: ${message}`)
        throw error
      }
    },
    [applicationsQuery, changeStatusMutation, pageIndex, pageSize, queryClient, selectedApplicationId],
  )

  const handleApprove = useCallback(
    (payload: { durationInMonths: number; amount: number }) =>
      handleStatusChange('APPROVED', {
        durationInMonths: payload.durationInMonths,
        amount: payload.amount,
      }),
    [handleStatusChange],
  )
  const handleReject = useCallback(
    (reason: string) => handleStatusChange('REJECTED', { rejectionReason: reason }),
    [handleStatusChange],
  )

  const handleRequestCorrection = useCallback(
    async (comment: string) => {
      if (!selectedApplicationId) return

      try {
        await requestCorrectionMutation.mutateAsync({
          id: selectedApplicationId,
          data: { comment },
        })

        await Promise.all([
          applicationsQuery.refetch(),
          queryClient.invalidateQueries({
            queryKey: getGetApplicationByIdQueryKey(selectedApplicationId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetApprovedApplicationsWithDetailsQueryKey({
              page: pageIndex,
              size: pageSize,
            }),
          }),
        ])
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unexpected error occurred'
        toast.error(`Unable to request corrections: ${message}`)
        throw error
      }
    },
    [
      applicationsQuery,
      pageIndex,
      pageSize,
      queryClient,
      requestCorrectionMutation,
      selectedApplicationId,
    ],
  )

  const isInitialLoading = applicationsQuery.isPending && !applicationsQuery.data;
  const isError = applicationsQuery.isError;
  const hasNoData = !isInitialLoading && !isError && totalApplications === 0;

  if (isInitialLoading) {
    return <ApplicationListSkeleton />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            Review approved members, see their subscription details, and adjust their status if needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load members</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>
                  {applicationsQuery.error instanceof Error
                    ? applicationsQuery.error.message
                    : 'We could not load the member list.'}
                </span>
                <Button variant="outline" size="sm" onClick={() => applicationsQuery.refetch()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : hasNoData ? (
            <div className="flex flex-col items-center justify-center space-y-3 rounded-md border border-dashed border-muted-foreground/30 bg-muted/10 p-12 text-center">
              <AlertCircle className="h-10 w-10 text-muted-foreground" />
              <h3 className="text-lg font-semibold">No members yet</h3>
              <p className="text-sm text-muted-foreground">
                Approved members will appear here once processed.
              </p>
            </div>
            ) : (
            <EnhancedDataTable
              data={rows}
              columns={columns}
              enablePagination
              enableSorting
              enableGlobalFilter
              enableExport
              enableColumnVisibility
              initialColumnVisibility={
                {email: false, membershipStartAt: false, status: false}
              }
              searchPlaceholder="Search members…"
              customButtons={
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="mr-2 h-4 w-4" />
                        Category
                        {selectedCategory !== 'All' && (
                          <Badge variant="secondary" className="ml-2">
                            {selectedCategory}
                          </Badge>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {categories.map((category) => (
                        <DropdownMenuItem
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category)
                            setTablePagination({ pageIndex: 0, pageSize: 10 })
                          }}
                          className={selectedCategory === category ? 'bg-accent' : ''}
                        >
                          {category}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Filter className="mr-2 h-4 w-4" />
                        Renewal Status
                        {selectedRenewalStatus !== 'All' && (
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {selectedRenewalStatus === 'Expiring Soon (≤30 days)' ? 'Expiring' : selectedRenewalStatus}
                          </Badge>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {renewalStatusOptions.map((status) => (
                        <DropdownMenuItem
                          key={status}
                          onClick={() => {
                            setSelectedRenewalStatus(status)
                            setTablePagination({ pageIndex: 0, pageSize: 10 })
                          }}
                          className={selectedRenewalStatus === status ? 'bg-accent' : ''}
                        >
                          {status}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              }
              onRowClick={(row) => handleRowClick(row)}
              loading={applicationsQuery.isFetching}
              paginationCallbacks={{
                manualPagination: true,
                totalCount: totalApplications,
                onPageChange: (nextPageIndex, nextPageSize) =>
                  setTablePagination((prev) => {
                    if (prev.pageIndex === nextPageIndex && prev.pageSize === nextPageSize) {
                      return prev;
                    }

                    return {
                      pageIndex: nextPageIndex,
                      pageSize: nextPageSize,
                    };
                  }),
                onPageSizeChange: (nextPageSize) =>
                  setTablePagination((prev) => {
                    if (prev.pageSize === nextPageSize && prev.pageIndex === 0) {
                      return prev;
                    }

                    return {
                      pageIndex: 0,
                      pageSize: nextPageSize,
                    };
                  }),
              }}
            />
          )}
        </CardContent>
      </Card>

      {selectedApplicationId !== null ? (
        <ApplicationDetailsDrawer
          applicationId={selectedApplicationId}
          open={drawerOpen}
          onOpenChange={handleDrawerOpenChange}
          initialData={selectedApplication as AdminApplication | undefined}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestCorrection={handleRequestCorrection}
          isProcessing={changeStatusMutation.isPending || requestCorrectionMutation.isPending}
        />
      ) : null}
    </div>
  );
}
